import { useState } from 'react';
import { AutoComplete } from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { Tag } from 'primereact/tag';
import { TooltipOptions } from 'primereact/tooltip/tooltipoptions';
import { CIE10Item } from '../../types/evolution.types';
import { axiosInstance } from '../../../../App';

const TOOLTIP_OPTIONS: TooltipOptions = { className: 'nv-tooltip', event: 'both', position: 'top' };

async function searchDiseases(query: string): Promise<CIE10Item[]> {
  try {
    const response = await axiosInstance.get('/api/secured/agenda/evolution/diseases', {
      params: { query }
    });
    return response.data;
  } catch {
    return [];
  }
}

interface CIE10ScorePanelProps {
  items: CIE10Item[];
  onChange: (items: CIE10Item[]) => void;
}

// Se monta dentro del editor de la evolución (nv-scope); sus estilos viven en MedicalEvolutionModal.scss.
export default function CIE10ScorePanel({ items, onChange }: CIE10ScorePanelProps) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<CIE10Item[]>([]);
  const [selectedDisease, setSelectedDisease] = useState<CIE10Item | null>(null);
  const [observation, setObservation] = useState('');
  const [saveAsAntecedent, setSaveAsAntecedent] = useState(false);

  const handleSearch = async (event: { query: string }) => {
    const results = await searchDiseases(event.query);
    setSuggestions(results);
  };

  const handleAdd = () => {
    if (!selectedDisease) return;
    const newItem: CIE10Item = {
      ...selectedDisease,
      observation,
      saveAsAntecedent,
    };
    // Avoid duplicates
    if (!items.some(i => i.diseaseId === newItem.diseaseId)) {
      onChange([...items, newItem]);
    }
    setSelectedDisease(null);
    setQuery('');
    setObservation('');
    setSaveAsAntecedent(false);
  };

  const handleRemove = (diseaseId: number) => {
    onChange(items.filter(i => i.diseaseId !== diseaseId));
  };

  return (
    <div className="cie10-panel">
      <div className="cie10-panel__form">
        <div className="nv-field">
          <label htmlFor="cie10-disease-search">Diagnóstico CIE-10</label>
          <AutoComplete
            inputId="cie10-disease-search"
            value={query}
            suggestions={suggestions}
            completeMethod={handleSearch}
            field="name"
            onChange={(e) => setQuery(e.value)}
            onSelect={(e) => { setSelectedDisease(e.value); setQuery(e.value.name); }}
            placeholder="Buscar por código o nombre"
            inputClassName="cie10-panel__search-input"
            panelClassName="nv-scope"
            itemTemplate={(item: CIE10Item) => (
              <span className="cie10-panel__option">
                <span className="nv-strong">{item.cie10Code}</span>
                <span>{item.name}</span>
              </span>
            )}
          />
        </div>
        <div className="cie10-panel__details">
          <div className="nv-field cie10-panel__observation-field">
            <label htmlFor="cie10-observation">Observación</label>
            <InputText
              id="cie10-observation"
              value={observation}
              onChange={(e) => setObservation(e.target.value)}
              placeholder="Agregar una observación opcional"
            />
          </div>
          <div className="nv-check-row cie10-panel__antecedent-check">
            <Checkbox
              inputId="cie10-save-antecedent"
              checked={saveAsAntecedent}
              onChange={(event) => setSaveAsAntecedent(Boolean(event.checked))}
            />
            <label htmlFor="cie10-save-antecedent">Guardar como antecedente</label>
          </div>
          <div className="cie10-panel__add">
            <Button
              type="button"
              className="buttonMain2"
              outlined
              icon="pi pi-plus-circle"
              label="Agregar diagnóstico"
              onClick={handleAdd}
              disabled={!selectedDisease}
              tooltip={selectedDisease ? undefined : 'Elegí un diagnóstico primero'}
              tooltipOptions={{ ...TOOLTIP_OPTIONS, showOnDisabled: true }}
            />
          </div>
        </div>
      </div>

      {items.length > 0 ? (
        <ul className="cie10-panel__list">
          {items.map((item) => (
            <li key={item.diseaseId} className="cie10-panel__item">
              <span className="nv-strong cie10-panel__code">{item.cie10Code}</span>
              <span className="cie10-panel__name">{item.name}</span>
              {item.observation && (
                <span className="nv-help cie10-panel__obs">{item.observation}</span>
              )}
              {item.saveAsAntecedent && (
                <Tag className="nv-tag-selected" value="Antecedente" />
              )}
              <Button
                type="button"
                text
                className="nv-icon"
                icon="pi pi-trash"
                aria-label={`Quitar ${item.name}`}
                tooltip="Quitar"
                tooltipOptions={TOOLTIP_OPTIONS}
                onClick={() => handleRemove(item.diseaseId)}
              />
            </li>
          ))}
        </ul>
      ) : (
        <p className="nv-muted cie10-panel__empty">Sin diagnósticos cargados</p>
      )}
    </div>
  );
}
